/**
 * KAN-960: правила модалки "Следующий контакт" (одно место для всех кнопок).
 * Виды - типы шага человека с backend (next_step_types.HUMAN_TYPES): "Звонок"
 * (manager_recall, в истории "Перезвон менеджера") и "Личная встреча"
 * (manager_meeting). Время вводится по поясу организации (KAN-840) и не может
 * быть в прошлом; текущая минута ещё не прошлое (backend даёт минуту допуска).
 */
import { ANY_UUID } from './managerAccess';
import { isoToZonedInput, zonedInputToIso } from './orgTimezone';
import type { Client } from '@/types/client';
import type { ManagerBrief } from '@/types/manager';

export type NextContactType = 'manager_recall' | 'manager_meeting';

export const NEXT_CONTACT_LABEL = 'Следующий контакт';
/** Подсказка кнопки "+" в пустой ячейке таблицы CRM. */
export const NEXT_CONTACT_CREATE_TITLE = 'Создать контакт';
export const NEXT_CONTACT_SAVED_TEXT = 'Следующий контакт записан, клиент в работе';
export const NEXT_CONTACT_TIME_REQUIRED = 'Укажите дату и время контакта';
export const NEXT_CONTACT_TIME_PAST = 'Дата и время контакта не могут быть в прошлом';
export const NEXT_CONTACT_FAILED = 'Не сохранено. Повторите.';
export const NEXT_CONTACT_CLEAR_LABEL = 'Убрать контакт';
export const NEXT_CONTACT_CLEARED_TEXT = 'Контакт убран';
/** Подпись шага без типа (как DEFAULT_STEP_LABEL backend). */
export const DEFAULT_STEP_LABEL = 'Следующий шаг';
/** Адрес для проверки доступа роли MANAGER (useCanCallApi). */
export const NEXT_CONTACT_ACCESS_URL = `/api/v1/clients/${ANY_UUID}/next-contact/`;

export const NEXT_CONTACT_TYPES: ReadonlyArray<{ value: NextContactType; label: string }> = [
  { value: 'manager_recall', label: 'Звонок' },
  { value: 'manager_meeting', label: 'Личная встреча' },
];

const MINUTE_MS = 60_000;

/** Время из поля по поясу организации: ISO или текст ошибки. */
export function checkContactTime(
  input: string,
  timeZone: string,
  now: number = Date.now()
): { iso: string } | { error: string } {
  const iso = zonedInputToIso(input, timeZone);
  if (!iso) return { error: NEXT_CONTACT_TIME_REQUIRED };
  // Поле с точностью до минуты: начало текущей минуты ещё не прошлое.
  if (new Date(iso).getTime() < Math.floor(now / MINUTE_MS) * MINUTE_MS) return { error: NEXT_CONTACT_TIME_PAST };
  return { iso };
}

/** Вид по умолчанию: текущий шаг клиента, если это встреча, иначе звонок. */
export function initialContactType(followupType: string | null | undefined): NextContactType {
  return followupType === 'manager_meeting' ? 'manager_meeting' : 'manager_recall';
}

/** Время по умолчанию: текущий шаг клиента, если он ещё впереди; иначе пусто. */
export function initialContactInput(
  followupAt: string | null | undefined,
  timeZone: string,
  now: number = Date.now()
): string {
  if (!followupAt || new Date(followupAt).getTime() < now) return '';
  return isoToZonedInput(followupAt, timeZone);
}

/** Клиент, которому назначается контакт. */
export interface NextContactTarget {
  clientId: string;
  name: string;
  manager: ManagerBrief | null;
  /** Текущий шаг клиента: начальные вид и время. */
  followupType?: string | null;
  followupAt?: string | null;
  /** Подпись текущего шага ("Визит с презентацией"); пусто - шага на карточке нет (кнопки "Убрать" нет). */
  followupLabel?: string | null;
}

/** Цель модалки из карточки клиента (чат, карточка, приложение менеджера). */
export function clientNextContactTarget(
  client: Pick<Client, 'id' | 'name' | 'phone' | 'manager' | 'followup_type' | 'followup_at' | 'followup_type_label'>
): NextContactTarget {
  return {
    clientId: client.id,
    name: client.name || client.phone || '',
    manager: client.manager ?? null,
    followupType: client.followup_type ?? null,
    followupAt: client.followup_at ?? null,
    followupLabel: client.followup_at ? client.followup_type_label || DEFAULT_STEP_LABEL : null,
  };
}
